import React from 'react';
import { Box, Button, Typography, useTheme, useMediaQuery } from '@mui/material';
import { TravelDate } from '../types/cms';

interface StickyBookingBarProps {
  packageTitle: string;
  selectedDate: TravelDate | null;
  priceFrom?: number;
  whatsappUrl: string;
  urgencyMessage?: string | null;
}

function StickyBookingBar({
  packageTitle,
  selectedDate,
  priceFrom,
  whatsappUrl,
  urgencyMessage,
}: StickyBookingBarProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Función para obtener el símbolo de moneda
  const getCurrencySymbol = (currency?: string) => {
    const symbols: Record<string, string> = {
      USD: 'USD',
      ARS: '$',
      EUR: '€',
      BRL: 'R$',
      PYG: '₲',
      UYU: '$U',
    };
    return symbols[currency || 'USD'] || 'USD';
  };

  // Obtener el precio a mostrar
  const getDisplayPrice = () => {
    if (selectedDate?.priceDBL) {
      const currencySymbol = getCurrencySymbol(selectedDate.currency);
      return `${currencySymbol} ${selectedDate.priceDBL.toLocaleString().replace(/,/g, '.')}`;
    }
    if (priceFrom) {
      return `USD ${priceFrom.toLocaleString().replace(/,/g, '.')}`;
    }
    return null;
  };

  // Obtener el mensaje de urgencia
  const getUrgencyText = () => {
    // Prioridad 1: Mensaje desde API
    if (urgencyMessage) {
      return urgencyMessage;
    }
    
    // Prioridad 2: Si está agotado
    if (selectedDate?.isAvailable === 'sold_out') {
      return 'Agotado - Consultá por próximas fechas';
    }
    
    // Default
    return 'Asegura tu lugar ahora';
  };

  const displayPrice = getDisplayPrice();
  const urgencyText = getUrgencyText();

  // Solo mostrar en móvil
  if (!isMobile) {
    return null;
  }

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 1100, // Por encima del WhatsAppFab
        backgroundColor: '#fff',
        boxShadow: '0 -2px 10px rgba(0, 0, 0, 0.1)',
        borderTop: '1px solid rgba(0, 0, 0, 0.1)',
        px: 2,
        py: 1.5,
        display: 'flex',
        alignItems: 'center',
        gap: 2,
      }}
    >
      {/* Columna izquierda: Información de urgencia y precio (60%) */}
      <Box
        sx={{
          flex: '0 0 60%',
          display: 'flex',
          flexDirection: 'column',
          gap: 0.5,
        }}
      >
        <Typography
          variant="body2"
          sx={{
            fontSize: '0.75rem',
            color: selectedDate?.isAvailable === 'sold_out' ? 'text.secondary' : 'error.main',
            fontWeight: 600,
            lineHeight: 1.2,
          }}
        >
          {urgencyText}
        </Typography>
        {displayPrice && (
          <Typography
            variant="body1"
            sx={{
              fontSize: '1rem',
              fontWeight: 700,
              color: 'primary.main',
              lineHeight: 1.2,
            }}
          >
            {displayPrice}
            {selectedDate?.priceDBL && (
              <Typography
                component="span"
                sx={{
                  fontSize: '0.75rem',
                  fontWeight: 400,
                  color: 'text.secondary',
                  ml: 0.5,
                }}
              >
                p/persona
              </Typography>
            )}
          </Typography>
        )}
      </Box>

      {/* Columna derecha: Botón de acción (40%) */}
      <Box
        sx={{
          flex: '0 0 40%',
          pr: 2,
        }}
      >
        <Button
          variant="contained"
          color="primary"
          fullWidth
          href={whatsappUrl}
          target="_blank"
          sx={{
            py: 1.5,
            fontSize: '0.875rem',
            fontWeight: 700,
            textTransform: 'uppercase',
            borderRadius: 2,
            boxShadow: 2,
            '&:hover': {
              boxShadow: 4,
            },
          }}
        >
          RESERVAR
        </Button>
      </Box>
    </Box>
  );
}

export default StickyBookingBar;

